// geofencesStore.ts
import { create } from "zustand";
import type { CustomAlertProps } from "../../components/base/CustomAlert";
import { getError } from "../../utils/getError";
import type { TFunction } from "i18next";
import { type UseFormReset } from "react-hook-form";
import api, { isAbortError } from "../../utils/api";
import { getAdminFetchGeneration } from "../../utils/adminFetchGeneration";

export interface Geofence {
  id: string;
  name: string;
  address: string;
  type: string; // warehouse, restrictedArea, customer, route, maintenance, authorizedStop, truck_yard
  // Set only on geofences owned by a truck yard (type === "truck_yard").
  // These are system-managed and read-only in the geofences panel; create/
  // edit/delete happen from the Patios section.
  truck_yard_id?: string;
  shape: string; // circular | linear | polygonal
  status_id: string; // ID del status en el store de statuses
  description: string;
  color: string;
  // Fechas eliminadas - ahora son opcionales en el backend
  // start_timestamp: string;
  // finish_timestamp: string;
  area?: number; // Área en metros cuadrados (solo para polygonales)
  geofenceActions: string[];
  notifiedUsers: string[];
  route_id?: string;
  coordinates:
    | { latitude: number; longitude: number }[]
    | { latitude: number; longitude: number; radius: number };
  coordinates_dense?: { latitude: number; longitude: number }[];
  coordinates_source?: "google" | "mapbox" | "straight" | null;
  buffer?: number;
  alerts: string[];
  devices: string[];
  timezone?: string;
  // Si es true, además del push FCM la geocerca envía correos a notifiedUsers.
  sendEmails?: boolean;
  createdAt: { _seconds: number; _nanoseconds: number };
  updatedAt: { _seconds: number; _nanoseconds: number };
}

interface GeofencesStore {
  geofences: Geofence[];
  loading: boolean;
  error: string | null;
  sidebarMeta?: {
    totalItems: number;
  };
  meta?: {
    error: string | null;
    count: number;
    status: number;
    currentPage: number;
    totalPages: number;
    totalItems: number;
    url: string;
  };
  fetchGeofences: (params?: {
    limit?: number;
    page?: number;
    offset?: number;
    status?: string;
    type?: string;
    shape?: string;
    search?: string;
  }) => Promise<void>;
  updateGeofenceInStore: (
    geofenceId: string,
    updatedGeofence: Partial<Geofence>
  ) => void;
  addGeofenceToStore: (geofence: Geofence) => void;
  removeGeofenceFromStore: (geofenceId: string) => void;
}

export const useGeofencesStore = create<GeofencesStore>((set, get) => ({
  geofences: [],
  loading: false,
  error: null,
  meta: undefined,
  sidebarMeta: undefined,

  fetchGeofences: async (params = {}) => {
    if (get().loading) {
      return;
    }
    const fetchGen = getAdminFetchGeneration();

    set({ loading: true, error: null });
    try {
      const queryParams: Record<string, any> = {
        limit: params.limit ?? 6,
        page: params.page ?? 1,
      };

      if (params.status && params.status !== "all") {
        queryParams.status = params.status;
      }
      if (params.type && params.type !== "all") {
        queryParams.type = params.type;
      }
      if (params.shape && params.shape !== "all") {
        queryParams.shape = params.shape;
      }
      if (params.search) {
        queryParams.search = params.search;
      }

      const { data } = await api.get("/geofenceList", { params: queryParams });

      if (getAdminFetchGeneration() !== fetchGen) return;
      // Only update sidebarMeta when fetching without a shape filter
      // so the sidebar badge is never overwritten by section-level filtered fetches.
      const isUnfiltered = !params.shape || params.shape === "all";
      set((prev) => ({
        geofences: data.data || [],
        meta: data.meta,
        loading: false,
        sidebarMeta: isUnfiltered && data.meta
          ? { totalItems: data.meta.totalItems }
          : prev.sidebarMeta,
      }));
    } catch (err: any) {
      if (getAdminFetchGeneration() !== fetchGen || isAbortError(err)) return;
      set({
        error: getError(err),
        loading: false,
      });
    }
  },

  updateGeofenceInStore: (
    geofenceId: string,
    updatedGeofence: Partial<Geofence>
  ) => {
    set((state) => ({
      geofences: state.geofences.map((geofence) =>
        geofence.id === geofenceId
          ? { ...geofence, ...updatedGeofence }
          : geofence
      ),
    }));
  },

  addGeofenceToStore: (geofence: Geofence) => {
    set((state) => ({
      geofences: [geofence, ...state.geofences],
      meta: state.meta
        ? {
            ...state.meta,
            count: state.meta.count + 1,
            totalItems: state.meta.totalItems + 1,
          }
        : undefined,
    }));
  },

  removeGeofenceFromStore: (geofenceId: string) => {
    set((state) => ({
      geofences: state.geofences.filter(
        (geofence) => geofence.id !== geofenceId
      ),
      meta: state.meta
        ? {
            ...state.meta,
            count: state.meta.count - 1,
            totalItems: state.meta.totalItems - 1,
          }
        : undefined,
    }));
  },
}));

export const updateGeofence = async (
  geofenceId: string,
  geofenceData: any,
  showAlert: (config: CustomAlertProps) => void,
  setLoadingSubmit: (loading: boolean) => void,
  t: TFunction<"translation", undefined>,
  onClose: () => void,
  reset?: UseFormReset<any>
) => {
  try {
    await api.put("/geofence", geofenceData, {
      params: { id: geofenceId },
    });

    setLoadingSubmit(false);
    showAlert({
      title: t("Exito"),
      text: t("Geocerca actualizada exitosamente"),
      icon: "success",
      confirmButtonText: "OK",
      confirmButtonColor: "#2563eb",
      onConfirm: () => {
        onClose();
        if (reset) reset();
      },
      showCancelButton: false,
    });
  } catch (error) {
    // Ignorar errores de cancelación (logout, cambio de compañía, etc.)
    if (isAbortError(error)) return;

    setLoadingSubmit(false);
    showAlert({
      title: t("Error"),
      text: getError(error, t),
      icon: "error",
      confirmButtonText: "OK",
      confirmButtonColor: "#2563eb",
      onConfirm: () => {},
    });
  }
};

export const createGeofence = async (
  geofenceData: any,
  showAlert: (config: CustomAlertProps) => void,
  setLoadingSubmit: (loading: boolean) => void,
  t: TFunction<"translation", undefined>,
  onClose: () => void,
  reset?: UseFormReset<any>
) => {
  try {
    await api.post("/geofence", geofenceData);

    setLoadingSubmit(false);
    showAlert({
      title: t("Exito"),
      text: t("Geocerca creada exitosamente"),
      icon: "success",
      confirmButtonText: "OK",
      confirmButtonColor: "#2563eb",
      onConfirm: () => {
        onClose();
        if (reset) reset();
      },
      showCancelButton: false,
    });
  } catch (error) {
    // Ignorar errores de cancelación (logout, cambio de compañía, etc.)
    if (isAbortError(error)) return;

    setLoadingSubmit(false);
    showAlert({
      title: t("Error"),
      text: getError(error, t),
      icon: "error",
      confirmButtonText: "OK",
      confirmButtonColor: "#2563eb",
      onConfirm: () => {},
    });
  }
};

export const deleteGeofence = async (
  geofenceId: string,
  showAlert: (config: CustomAlertProps) => void,
  setLoadingSubmit: (loading: boolean) => void,
  t: TFunction<"translation", undefined>,
  onClose: () => void
) => {
  try {
    await api.delete("/geofence", { params: { id: geofenceId } });

    useGeofencesStore.getState().removeGeofenceFromStore(geofenceId);

    setLoadingSubmit(false);
    showAlert({
      title: t("Exito"),
      text: t("Geocerca eliminada exitosamente"),
      icon: "success",
      confirmButtonText: "OK",
      confirmButtonColor: "#2563eb",
      onConfirm: () => {
        onClose();
      },
      showCancelButton: false,
    });
  } catch (error) {
    // Ignorar errores de cancelación (logout, cambio de compañía, etc.)
    if (isAbortError(error)) return;

    setLoadingSubmit(false);
    showAlert({
      title: t("Error"),
      text: getError(error, t),
      icon: "error",
      confirmButtonText: "OK",
      confirmButtonColor: "#2563eb",
      onConfirm: () => {},
    });
  }
};
